import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { Skeleton } from 'antd'
import type { BasicDataNode, TreeProps } from 'antd/es/tree'
import {
    MOPTRestrictionDialog,
    MOPT_RESTRICTION_MESSAGES,
    isMOPTArticle,
    useIsMOPTDialogOpen,
    useMOPTDialogActions,
    useMOPTRestrictionType,
} from 'components/WorkspaceEditor/features/moptArticleRestriction'
import {
    ContributorRestrictionDialog,
    InactivePTActivationWarning,
    PTDeactivationWarning,
    useContributorRestrictionDialogActions,
    useIsContributorRestrictionDialogOpen,
    useIsPTActivationDialogOpen,
    useIsPTDeactivationDialogOpen,
    usePTActivationDialogActions,
    usePTActivationDialogData,
    usePTDeactivationDialogActions,
    usePTDeactivationDialogData,
} from 'components/WorkspaceEditor/features/ptActivation'
import { useReorderItems } from 'components/WorkspaceEditor/hooks/useReorderItems'
import {
    useDragAnchorElement,
    useIsProductTypeDeactivationDialogOpen,
} from 'components/WorkspaceEditor/HoveredItemStore'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import ProductTypeDeactivationWarning from 'pages/ProjectsPage/WorkspacePage/components/Common/ProductTypeDeactivationWarning'
import React, { ComponentType, memo, useCallback, useRef } from 'react'
import { useNodeMapShallowSelector } from '../../features/keyOperations/store/NodeMapStore'
import { useScrollToNewContent } from '../../features/scrollToNewContent'
import {
    useResizeObserver,
    useTreeDataTransformer,
    useTreeHandlers,
} from '../../hooks'
import { useTreeViewContainerStyles, useTreeViewStyles } from '../../styles'
import type { DataNode, PartType, ScrollableTreeRef } from '../../types'
import { useActivationDeactivationController } from '../NodeContents/useActivationDeactivationController'
import { useProcessSuggestionCardsOnTreeEffect } from '../TrackChangesPanel/hooks/useProcessSuggestionCardsOnTreeEffect'
import { useScrollToTreeNode } from '../TrackChangesPanel/services/ScrollToTreeNode/hooks/useScrollToTreeNode'
import {
    NotUsedLabel,
    TreeLayout,
    useAutoExpandLoadingArticles,
} from '../TreeView'
import { useLoadingTransition } from './hooks/useLoadingTransition'
import { TreeViewPopovers } from './TreeViewPopovers/TreeViewPopovers'
import { withLazyLoadRef } from './withLazyLoadRef'

/** Maximum time to wait for content before showing "Not Used" (in milliseconds) */
const CONTENT_LOADING_TIMEOUT = 5000

/** Props for the TreeView component */
export interface TreeViewProps {
    /** Array of tree nodes */
    treeData: DataNode[]
    /** Function to fetch product family data */
    fetchProductFamily?: (
        partType: PartType,
        rootArticleContentItemIds?: string[]
    ) => void
    /** Function to fetch best practice element data */
    fetchBestPracticeElementData?: (elementId: string) => void
    /** Function to fetch project data */
    fetchProjectData?: () => void
    /** Flag indicating if the tree is loading */
    isLoading?: boolean
    /** Optional custom class name for the tree container */
    className?: string
    /** Optional tree reference for external control */
    treeRef?: React.MutableRefObject<ScrollableTreeRef>
    /** Project ID for header node */
    projectId?: string
    /** Selected project element node for header */
    selectedProjectElementNode?: ProjectElementNode
}

/**
 * Renders a hierarchy of Skeleton components for loading visualizations.
 *
 * @param {number} [props.level=3] - The level of nesting of the skeleton
 * components. A higher number will result in a more deeply nested
 * structure.
 */
export const LoadingSkeletons = memo<{ level?: number }>(({ level = 3 }) => (
    <>
        {Array.from({ length: level }).map((_, i) => (
            <div data-testid="ant-skeleton" key={i}>
                <Skeleton active />
            </div>
        ))}
    </>
))

/**
 * Create a lazy-loaded Tree component from antd library
 * @param {TreeProps<BasicDataNode | DataNode>} props - Component props
 * @returns {ReactElement} antd Tree component
 */
const LazyTree = withLazyLoadRef<TreeProps<BasicDataNode | DataNode>, any>(() =>
    import('antd/es/tree/Tree').then((module) => ({
        default: module.default as ComponentType<any>,
    }))
)

/**
 * TreeView component renders a tree structure with expandable nodes, dynamic loading, and custom styling.
 * @param {TreeViewProps} TreeViewProps - Component props
 * @returns {ReactElement} Rendered TreeView component
 */
export const TreeView = memo<TreeViewProps>(
    ({
        treeData = [],
        fetchProductFamily,
        fetchBestPracticeElementData,
        fetchProjectData,
        isLoading,
        className,
        treeRef: externalTreeRef,
        projectId,
        selectedProjectElementNode,
    }) => {
        const treeViewClasses = useTreeViewStyles()
        const containerClasses = useTreeViewContainerStyles()
        const resizeObserver = useResizeObserver()
        const dragAnchorElement = useDragAnchorElement()
        const { setDialogOpen: setMOPTDialogOpen } = useMOPTDialogActions()

        const {
            handleOnDrop,
            handleOnDragOver,
            handleDragStart: originalHandleDragStart,
            handleDragEnd,
            allowDrop,
        } = useReorderItems()

        // Wrap handleDragStart to show popup for MOPT articles
        const handleDragStart = useCallback(
            (info: any) => {
                const node = info.node as DataNode
                // Check if trying to drag MOPT article
                if (
                    node.type === 'Article' &&
                    isMOPTArticle(node.contentItemView)
                ) {
                    setMOPTDialogOpen(true, 'MOVE')
                    return
                }
                originalHandleDragStart()
            },
            [originalHandleDragStart, setMOPTDialogOpen]
        )

        const {
            articlesLoading,
            expandedKeys,
            isEditingPlainText,
        } = useShallowSelector((state) => ({
            articlesLoading: state.articlesLoading,
            expandedKeys: state.expandedKeys,
            isEditingPlainText: state.isEditingPlainText,
        }))

        const {
            isSaveOperationInProgress,
            elementsBeingAdded,
            recentlyAddedElements,
        } = useNodeMapShallowSelector((state) => ({
            isSaveOperationInProgress: state.isSaveOperationInProgress,
            elementsBeingAdded: state.elementsBeingAdded,
            recentlyAddedElements: state.recentlyAddedElements,
        }))

        const draggableConfig = React.useMemo(() => {
            if (isSaveOperationInProgress) return false
            if (isEditingPlainText) return false

            const checkNodeDraggable = (node: BasicDataNode) => {
                if (!('contentItemNodes' in node)) return true

                const dataNode = node as DataNode

                const contentItemNodes = dataNode.contentItemNodes
                if (!contentItemNodes || contentItemNodes.length === 0)
                    return true

                for (const contentItem of contentItemNodes) {
                    if (
                        contentItem?.id &&
                        (elementsBeingAdded.has(contentItem.id) ||
                            recentlyAddedElements.has(contentItem.id))
                    ) {
                        return false
                    }
                }

                return true
            }

            //If no elements are being processed, enable drag
            if (
                elementsBeingAdded.size === 0 &&
                recentlyAddedElements.size === 0
            ) {
                return { icon: false, nodeDraggable: checkNodeDraggable }
            }

            return {
                icon: false,
                nodeDraggable: checkNodeDraggable,
            }
        }, [
            isSaveOperationInProgress,
            isEditingPlainText,
            elementsBeingAdded,
            recentlyAddedElements,
        ])

        const internalTreeRef = useRef<ScrollableTreeRef>(
            {} as ScrollableTreeRef
        )
        const treeRef = externalTreeRef || internalTreeRef

        const {
            menuElement,
            handleExpandCollapseArticle,
            handleLoadData,
            handleExpand,
            handleScroll,
            handleMouseHover,
            handleMouseLeave,
            onClickManageOptions,
            onClickBPMenu,
            onClickNotesMenu,
            onCancelManageOptions,
            onSaveManageOptions,
            onCloseMainMenu,
            handleShowHideProperty,
            onClickMoveBlockMenu,
        } = useTreeHandlers(
            fetchProductFamily,
            fetchBestPracticeElementData,
            fetchProjectData
        )

        const transformedNodes = useTreeDataTransformer({
            treeData,
            classes: treeViewClasses,
            treeRef,
            projectId,
            selectedProjectElementNode,
            isLoading,
        })

        // Use custom hook to handle loading transitions and prevent "Not Used" flash
        const showLoadingState = useLoadingTransition(
            Boolean(isLoading),
            transformedNodes.length,
            CONTENT_LOADING_TIMEOUT
        )

        useAutoExpandLoadingArticles(
            articlesLoading,
            expandedKeys,
            handleExpandCollapseArticle,
            transformedNodes
        )

        useProcessSuggestionCardsOnTreeEffect(
            treeData,
            showLoadingState,
            articlesLoading
        )
        useScrollToTreeNode(treeRef, treeData)
        useScrollToNewContent(treeRef)

        return (
            <TreeLayout
                ref={resizeObserver?.treeContainerRef}
                className={containerClasses.treeContainer}
            >
                {showLoadingState ? (
                    <LoadingSkeletons data-testid="ant-skeleton" level={3} />
                ) : transformedNodes.length > 1 ? ( // Check > 1 because header node is always included
                    <>
                        <LazyTree
                            {...{
                                role: 'tree',
                                'data-testid': 'ant-tree',
                                'aria-label': 'Workspace Tree',
                            }}
                            ref={treeRef}
                            treeData={transformedNodes}
                            loadData={handleLoadData}
                            onExpand={handleExpand}
                            onScroll={handleScroll}
                            expandedKeys={expandedKeys}
                            className={containerClasses.tree}
                            height={resizeObserver?.containerHeight}
                            itemHeight={50}
                            itemScrollOffset={50}
                            virtual={true}
                            onContextMenu={(e) => e.preventDefault()}
                            onMouseEnter={handleMouseHover}
                            onMouseLeave={handleMouseLeave}
                            onDrop={handleOnDrop}
                            onDragOver={handleOnDragOver}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            allowDrop={allowDrop}
                            switcherIcon={false}
                            // TODO: remove isEditingPlainText when dnd cursor mode is resolved on Firefox
                            draggable={draggableConfig}
                        />
                        <TreeViewPopovers
                            onClickManageOptions={onClickManageOptions}
                            onClickBPMenu={onClickBPMenu}
                            onClickNotesMenu={onClickNotesMenu}
                            onCancelManageOptions={onCancelManageOptions}
                            onSaveManageOptions={onSaveManageOptions}
                            onCloseMainMenu={onCloseMainMenu}
                            handleShowHideProperty={handleShowHideProperty}
                            onClickMoveBlockMenu={onClickMoveBlockMenu}
                            states={{
                                dragIconElement: dragAnchorElement as HTMLElement,
                                menuElement,
                            }}
                        />

                        <TreeViewDialogs />
                    </>
                ) : (
                    <NotUsedLabel />
                )}
            </TreeLayout>
        )
    }
)

/**
 * Component that renders all dialogs used in the TreeView
 * - ProductTypeDeactivationWarning: Warning when deactivating a Product Type
 * - MOPTRestrictionDialog: MOPT article restriction warning
 * - InactivePTActivationWarning: Warning when activating content linked to inactive PT
 * - PTDeactivationWarning: Warning when deactivating content linked to active PT
 * - PTActivationProgressDialog: Progress bar shown while PT is being added to project
 */
const TreeViewDialogs = () => {
    // Product Type Deactivation Dialog
    const isProductTypeDeactivationDialogOpen = useIsProductTypeDeactivationDialogOpen()
    const {
        handleProductTypeWarningDialogClose,
    } = useActivationDeactivationController()

    // MOPT Restriction Dialog
    const isMOPTRestrictionDialogOpen = useIsMOPTDialogOpen()
    const moptRestrictionType = useMOPTRestrictionType()
    const { setDialogOpen: setMOPTDialogOpen } = useMOPTDialogActions()

    const handleMOPTRestrictionDialogClose = useCallback(() => {
        setMOPTDialogOpen(false)
    }, [setMOPTDialogOpen])

    const moptDialogContent = moptRestrictionType
        ? MOPT_RESTRICTION_MESSAGES[moptRestrictionType]
        : MOPT_RESTRICTION_MESSAGES.MOVE

    // PT Activation Dialog
    const isPTActivationDialogOpen = useIsPTActivationDialogOpen()
    const { ptName, onProceedCallback } = usePTActivationDialogData()
    const {
        closeDialog: closePTActivationDialog,
    } = usePTActivationDialogActions()

    const handlePTActivationDialogClose = useCallback(() => {
        closePTActivationDialog()
    }, [closePTActivationDialog])

    const handlePTActivationProceed = useCallback(async () => {
        if (onProceedCallback) {
            await onProceedCallback()
        }
    }, [onProceedCallback])

    // PT Deactivation Dialog
    const isPTDeactivationDialogOpen = useIsPTDeactivationDialogOpen()
    const { deactivationOnProceedCallback } = usePTDeactivationDialogData()
    const {
        closeDeactivationDialog: closePTDeactivationDialog,
    } = usePTDeactivationDialogActions()

    const handlePTDeactivationDialogClose = useCallback(() => {
        closePTDeactivationDialog()
    }, [closePTDeactivationDialog])

    const handlePTDeactivationProceed = useCallback(async () => {
        if (deactivationOnProceedCallback) {
            await deactivationOnProceedCallback()
        }
    }, [deactivationOnProceedCallback])

    // Contributor Restriction Dialog
    const isContributorRestrictionDialogOpen = useIsContributorRestrictionDialogOpen()
    const {
        closeContributorRestrictionDialog,
    } = useContributorRestrictionDialogActions()

    const handleContributorRestrictionDialogClose = useCallback(() => {
        closeContributorRestrictionDialog()
    }, [closeContributorRestrictionDialog])

    return (
        <>
            <ProductTypeDeactivationWarning
                openDialog={isProductTypeDeactivationDialogOpen}
                handleDialogClose={handleProductTypeWarningDialogClose}
            />

            <MOPTRestrictionDialog
                open={isMOPTRestrictionDialogOpen}
                onClose={handleMOPTRestrictionDialogClose}
                title={moptDialogContent.title}
                description={moptDialogContent.description}
            />

            <InactivePTActivationWarning
                open={isPTActivationDialogOpen}
                ptName={ptName}
                onClose={handlePTActivationDialogClose}
                onProceed={handlePTActivationProceed}
            />

            <PTDeactivationWarning
                open={isPTDeactivationDialogOpen}
                onClose={handlePTDeactivationDialogClose}
                onProceed={handlePTDeactivationProceed}
            />

            <ContributorRestrictionDialog
                open={isContributorRestrictionDialogOpen}
                onClose={handleContributorRestrictionDialogClose}
            />
        </>
    )
}
